/**
 * Whether an answer can actually be used, checked the same way on both sides.
 * @file src/shared/answers.ts
 *
 * ── What was wrong ────────────────────────────────────────────────────────
 * Every question was a text box and the only rule was "required". So an
 * email address with a comma for a dot, or a phone number with three digits,
 * was accepted, stored, and turned into a prospect nobody could reach — and
 * the follow-up sequence, the thing this product sells, then chased a client
 * who was never going to receive any of it. The firm's first sign of it is a
 * lead that went quiet, which reads like a cold prospect rather than a typo.
 *
 * ── Deliberately loose ────────────────────────────────────────────────────
 * These are not RFC validators and must not become them. A prospective
 * client mistyping their own address is the case being caught; a real
 * address refused because a regex disagrees with a plus sign or a long TLD
 * is far worse than the typo, because the client cannot argue with it and
 * leaves. Anything with one @, a dot after it and no spaces passes.
 *
 * ── Both sides, one file ──────────────────────────────────────────────────
 * The browser checks so the client is told while they are still looking at
 * the field; the server checks because a browser check is a suggestion. If
 * those ran different rules the client would be told a value was fine and
 * then refused on submit.
 *
 * @llm-rule WHEN: Deciding whether a contact answer is usable
 * @llm-rule AVOID: Tightening these into strict validators — see above
 */

/** What a question's answer is checked against, if anything. */
export type AnswerRule = 'email' | 'phone' | null;

/**
 * One @, something either side of it, a dot in the domain, no spaces.
 * Everything else — plus addressing, long TLDs, unicode local parts — is
 * somebody's real address and is none of our business.
 */
const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/;

/** Digits only, ignoring the spaces, dashes, dots and brackets people use. */
const DIGITS = /\d/g;

export function looksLikeEmail(value: string): boolean {
  return EMAIL.test(value.trim());
}

/**
 * Seven digits is the shortest real number anywhere (a local US number);
 * fifteen is the international maximum, and the slack above it is for people
 * who write an extension on the end.
 */
export function looksLikePhone(value: string): boolean {
  const digits = value.match(DIGITS)?.length ?? 0;
  return digits >= 7 && digits <= 20;
}

/**
 * What this question's answer has to look like.
 *
 * Driven by the question type AND by what it saves to, because the forms
 * that exist today ask for an email with a plain text question and a
 * `mapsTo` of 'email'. Keying only off the type would leave every one of
 * them unchecked.
 */
export function ruleFor(question: { type?: string; mapsTo?: string }): AnswerRule {
  if (question.type === 'email' || question.mapsTo === 'email') return 'email';
  if (question.type === 'phone' || question.mapsTo === 'phone') return 'phone';
  return null;
}

/**
 * The rule this answer breaks, or null.
 *
 * An empty answer never fails: whether it is allowed to be empty is what
 * `required` decides, and reporting both at once would tell a client their
 * blank email address is badly formatted.
 */
export function answerProblem(
  question: { type?: string; mapsTo?: string },
  value: unknown,
): AnswerRule {
  const rule = ruleFor(question);
  if (!rule) return null;
  if (typeof value !== 'string' || !value.trim()) return null;
  if (rule === 'email') return looksLikeEmail(value) ? null : 'email';
  return looksLikePhone(value) ? null : 'phone';
}

/**
 * Is this answer present at all?
 *
 * Separate from whether it is USABLE: an empty box and a mistyped address
 * are different complaints, and telling a client their blank email is badly
 * formatted is how a form argues with somebody who has not typed anything.
 */
export function answered(v: unknown): boolean {
  if (v === undefined || v === null || v === '') return false;
  if (Array.isArray(v)) return v.length > 0;
  return true;
}

/** The rows of a repeating block the client actually filled in. */
export function filledRows(value: unknown): Record<string, unknown>[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (row): row is Record<string, unknown> =>
      Boolean(row) &&
      typeof row === 'object' &&
      !Array.isArray(row) &&
      Object.values(row as Record<string, unknown>).some((v) => answered(v)),
  );
}

/**
 * Is this question answered, whatever kind it is?
 *
 * A repeating block full of empty rows is not answered — the client pressed
 * "Add another" and stopped, which is the opposite of having told us
 * something, and counting it would put an abandoned form at 100%.
 *
 * Structural rather than typed against Question, because the browser's copy
 * of a question is a different shape from the server's and the whole point
 * of this file is that the two agree.
 */
export function hasAnswer(
  question: { id: string; type?: string },
  answers: Record<string, unknown>,
): boolean {
  if (question.type === 'group') return filledRows(answers[question.id]).length > 0;
  return answered(answers[question.id]);
}
